/**
 * Browser cookie helpers for client-side cookie read/write/clear.
 *
 * Used as the source-of-truth carrier for the active-school identifier
 * (`sj-active-school-id`) replacing the previous localStorage path. The
 * cookie is server-readable so Next.js server components can resolve the
 * active school without depending on the Clerk-org JWT path.
 *
 * Phase 3.1 of the Authorization & Active-School initiative.
 *
 * Cookie attributes:
 * - Path=/         visible across the app
 * - Max-Age=1y     long-lived; cookie is just a school-id pointer
 * - SameSite=Lax   standard for first-party navigation
 * - Secure         set on HTTPS only (localhost dev uses HTTP)
 * - NOT HttpOnly   must be readable from JS for the API client header attach
 */

// Canonical cookie names for the active school. Every reader and writer imports
// them; no other file spells them.
export const ACTIVE_SCHOOL_COOKIE = 'sj-active-school-id'
/** Display name, so the switcher can paint before any fetch. */
export const ACTIVE_SCHOOL_NAME_COOKIE = 'sj-active-school-name'
/** JSON snapshot of the full school (logo, avatar) written by the switcher. */
export const ACTIVE_SCHOOL_SNAPSHOT_COOKIE = 'sj-active-school'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

export function setBrowserCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:'
  const secureFlag = isHttps ? '; Secure' : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secureFlag}`
}

export function getBrowserCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * The active-school id cookie, read now. For send-time checks outside React
 * render (inside a `queryFn`, beside the `x-school-id` the request carries).
 * React code reads `useActiveSchoolId()` from `school-context.tsx` instead.
 */
export function readActiveSchoolCookie(): string | null {
  return getBrowserCookie(ACTIVE_SCHOOL_COOKIE)
}

export function clearBrowserCookie(name: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`
}

/**
 * Writes the active-school cookies: the id always, the name when given, and the
 * JSON snapshot only when given. `SchoolProvider` is the writer; the post-login
 * verify screen calls this before the provider mounts.
 */
export function writeActiveSchoolCookies(school: { id: string; name?: string }, snapshot?: object): void {
  setBrowserCookie(ACTIVE_SCHOOL_COOKIE, school.id)
  if (school.name !== undefined) setBrowserCookie(ACTIVE_SCHOOL_NAME_COOKIE, school.name)
  if (snapshot === undefined) return
  try {
    setBrowserCookie(ACTIVE_SCHOOL_SNAPSHOT_COOKIE, JSON.stringify(snapshot))
  } catch {
    // Serialization issue — non-fatal; the reader falls back to id + name.
  }
}
